"use client";

import { useMemo } from "react";

import { Annotation, isCollectionAnnotation } from "@perceptron/core/types";

import { useStreamFollow } from "@demo/features/media/useStreamFollow";
import { useMediaCarouselStore } from "@demo/store/media-carousel";
import { AgentState, useModelStore } from "@demo/store/model";

/** The last leaf in document order — the most recently streamed result. */
function newestLeaf(nodes: readonly Annotation[]): Annotation | undefined {
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const node = nodes[i]!;
    if (!isCollectionAnnotation(node)) return node;
    const deeper = newestLeaf(node.children);
    if (deeper) return deeper;
  }
  return undefined;
}

/**
 * While a run streams, move the carousel to whichever asset the newest
 * annotation refers to, so results are seen as they arrive rather than only on
 * whichever asset happened to be open.
 *
 * Manual navigation wins, and says so explicitly: picking a thumbnail (or
 * activating an annotation) goes through `selectMediaManually`, which sets
 * `followSuspended` for the rest of the run. Nothing here watches the index to
 * guess what the viewer did — an earlier version did, and since
 * `setMediaInAgentCarousel` moves the index to the last asset as part of
 * starting a run, that guess both disabled following on every real run and
 * drove the carousel round in a loop.
 */
export function useFollowStreamingAsset(): void {
  const annotations = useModelStore((state) => state.annotations);
  const agentState = useModelStore((state) => state.agentState);
  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const activeIndex = useMediaCarouselStore((state) => state.currentMediaIndexInAgentCarousel);
  const followSuspended = useMediaCarouselStore((state) => state.followSuspended);
  const setActiveIndex = useMediaCarouselStore((state) => state.setCurrentMediaIndexInAgentCarousel);

  const isStreaming = agentState === AgentState.STREAMING || agentState === AgentState.EVALUATING;

  const targetIndex = useMemo(() => {
    if (media.length < 2) return null;
    const leaf = newestLeaf(annotations);
    return leaf ? leaf.assetIdx : null;
  }, [annotations, media.length]);

  useStreamFollow({
    activeIndex,
    enabled: isStreaming && !followSuspended,
    onSwitch: setActiveIndex,
    targetIndex,
  });
}
